// assets/km-cart-upsell.js

async function fetchJSON(url, options) {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    ...options,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function getCart() {
  return fetchJSON("/cart.js");
}

async function addVariant(variantId, quantity = 1, properties = {}) {
  return fetchJSON("/cart/add.js", {
    method: "POST",
    body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity), properties }),
  });
}

async function removeVariantFromCart(variantId) {
  const cart = await getCart();
  const line = cart.items.find((it) => Number(it.variant_id) === Number(variantId));
  if (!line) return cart;
  return fetchJSON("/cart/change.js", {
    method: "POST",
    body: JSON.stringify({ id: line.key, quantity: 0 }),
  });
}

// ── Lista de variants adicionados — persiste no sessionStorage ───────────
const _STORE_KEY = "km_upsell_hidden";
const _UPSELL_IDS_KEY = "km_upsell_ids"; // variants adicionados via upsell (para badge)

function _loadUpsellIds() {
  try { return new Set(JSON.parse(sessionStorage.getItem(_UPSELL_IDS_KEY) || "[]")); } catch { return new Set(); }
}
function _saveUpsellIds(set) {
  try { sessionStorage.setItem(_UPSELL_IDS_KEY, JSON.stringify([...set])); } catch {}
}

function _loadHidden() {
  try { return new Set(JSON.parse(sessionStorage.getItem(_STORE_KEY) || "[]")); } catch { return new Set(); }
}
function _saveHidden(set) {
  try { sessionStorage.setItem(_STORE_KEY, JSON.stringify([...set])); } catch {}
}

// Sincroniza com o carrinho real: remove IDs que não estão mais no carrinho
async function _syncHiddenWithCart() {
  try {
    const res = await fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
    const cart = await res.json();
    const cartVariantIds = new Set(cart.items.map((i) => String(i.variant_id)));
    const hidden = _loadHidden();
    // Mantém no set só os que ainda estão no carrinho
    for (const vid of hidden) {
      if (!cartVariantIds.has(vid)) hidden.delete(vid);
    }
    _saveHidden(hidden);
  } catch {}
}

function hideAddedCards() {
  const hidden = _loadHidden();
  if (!hidden.size) return;
  document.querySelectorAll("[data-km-upsell-card]").forEach((card) => {
    const btn = card.querySelector("[data-km-upsell-toggle]");
    if (!btn) return;
    const vid = String(btn.getAttribute("data-km-variant-id") || "");
    if (hidden.has(vid)) {
      card.style.setProperty("display", "none", "important");
    }
  });
}

// Reesconde sempre que o drawer for refreshado (DOM novo)
// Lê os variant IDs diretamente do DOM renderizado (sem fetch extra / sem cache)
function _syncHiddenWithDOM() {
  // Coleta todos os variant IDs que estão atualmente nos itens do carrinho no DOM
  const cartVariantIds = new Set();
  document.querySelectorAll("[data-variant-id], [data-cart-item-variant-id]").forEach((el) => {
    const vid = el.dataset.variantId || el.dataset.cartItemVariantId;
    if (vid) cartVariantIds.add(String(vid));
  });
  // Também lê dos inputs de quantity que o tema renderiza
  document.querySelectorAll(".cart-items__quantity [name='updates[]'], input[data-quantity-variant-id]").forEach((el) => {
    const vid = el.dataset.quantityVariantId || el.getAttribute("id")?.replace("updates_", "");
    if (vid) cartVariantIds.add(String(vid));
  });
  // Remove do sessionStorage qualquer ID que não esteja mais no DOM do carrinho
  const hidden = _loadHidden();
  let changed = false;
  for (const vid of hidden) {
    if (!cartVariantIds.has(vid)) {
      hidden.delete(vid);
      changed = true;
    }
  }
  if (changed) _saveHidden(hidden);
  return hidden;
}

document.addEventListener("km:cart-drawer:refreshed", () => {
  _syncHiddenWithDOM();
  hideAddedCards();
  checkUpsellEmpty();
  // Injeta badges imediatamente
  injectUpsellBadges();
  // Sync async com /cart.js + segundo passe
  _syncUpsellIdsWithDOM().then(() => {
    injectUpsellBadges();
  });
  setTimeout(() => {
    _syncHiddenWithDOM();
    hideAddedCards();
    checkUpsellEmpty();
    injectUpsellBadges();
  }, 300);
});

// ── Esconde bloco upsell se todos os cards foram adicionados ─────────────────
function checkUpsellEmpty() {
  document.querySelectorAll("[data-km-upsell-root]").forEach((root) => {
    const cards = root.querySelectorAll("[data-km-upsell-card]");
    const anyVisible = Array.from(cards).some(
      (c) => c.style.display !== "none" && !c.hasAttribute("data-km-added")
    );
    // Esconde só o head (título/subtítulo) e o scroll-wrap, não o root inteiro
    const head = root.querySelector(".km-upsell__head");
    const wrap = root.querySelector(".km-upsell__scroll-wrap");
    if (head) head.style.setProperty("display", anyVisible ? "" : "none", "important");
    if (wrap) wrap.style.setProperty("display", anyVisible ? "" : "none", "important");
    // Remove padding/border do root quando vazio
    if (anyVisible) {
      root.style.removeProperty("padding");
      root.style.removeProperty("border-top");
    } else {
      root.style.setProperty("padding", "0", "important");
      root.style.setProperty("border-top", "none", "important");
    }
  });
}

// ── Carousel arrow binding ──────────────────────────────────────────────────
function initCarousels() {
  document.querySelectorAll("[data-km-upsell-root]").forEach((root) => {
    // Reset bind flag se o root for novo (após DOM swap)
    const track = root.querySelector("[data-km-track]");
    if (!track) return;
    if (track.__kmBound) return;
    track.__kmBound = true;

    const nextBtn = root.querySelector(".km-upsell__next");
    const prevBtn = root.querySelector(".km-upsell__prev");

    function getCards() {
      return Array.from(track.querySelectorAll(".km-upsell__card:not([style*='display: none'])"));
    }

    function currentIndex() {
      const cards = getCards();
      let best = 0, bestDist = Infinity;
      cards.forEach((c, i) => {
        const dist = Math.abs(c.offsetLeft - track.offsetLeft - track.scrollLeft);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    function scrollToCard(index) {
      const cards = getCards();
      const card = cards[Math.max(0, Math.min(index, cards.length - 1))];
      if (!card) return;
      track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: "smooth" });
    }

    function updateArrows() {
      const max = track.scrollWidth - track.clientWidth;
      const hasScroll = max > 4;
      const arrowsRow = root.querySelector(".km-upsell__arrows-row");
      // Mostra a row so se tiver mais de 1 card para scrollar
      if (arrowsRow) arrowsRow.style.display = hasScroll ? "flex" : "none";
      // Ambas as setas sempre visiveis quando ha scroll
      if (prevBtn) prevBtn.style.display = hasScroll ? "flex" : "none";
      if (nextBtn) nextBtn.style.display = hasScroll ? "flex" : "none";
    }

    if (nextBtn) nextBtn.addEventListener("click", () => scrollToCard(currentIndex() + 1));
    if (prevBtn) prevBtn.addEventListener("click", () => scrollToCard(currentIndex() - 1));
    track.addEventListener("scroll", updateArrows, { passive: true });

    // Inicializa estado das arrows apos layout calcular
    requestAnimationFrame(() => requestAnimationFrame(updateArrows));
  });

  // Reesconde cards pendentes após qualquer init
  hideAddedCards();
  // Esconde o bloco inteiro se não restar nenhum card visível
  checkUpsellEmpty();
}

// ── Toggle: delegated on document so it survives DOM swaps ─────────────────
document.addEventListener("click", async (e) => {
  const btn = e.target.closest("[data-km-upsell-toggle]");
  if (!btn) return;

  const root = btn.closest("[data-km-upsell-root]");
  const sectionId = root?.getAttribute("data-km-section-id");
  if (!sectionId) return;

  const card = btn.closest("[data-km-upsell-card]");
  const variantSelect = card?.querySelector("[data-km-upsell-variant]");
  const variantId = String(variantSelect?.value || btn.getAttribute("data-km-variant-id") || "");
  if (!variantId) return;

  const pressed = btn.getAttribute("aria-pressed") === "true";
  btn.classList.add("is-loading");

  try {
    if (!pressed) {
      // Lê o % de desconto configurado no bloco (data-km-discount-pct no root)
      const discountPct = root?.getAttribute("data-km-discount-pct") || "";
      const properties = discountPct ? { "_km_upsell_pct": discountPct + "% OFF" } : {};
      await addVariant(variantId, 1, properties);
      // Registra para esconder mesmo após DOM swap
      const _h = _loadHidden(); _h.add(variantId); _saveHidden(_h);
      // Registra como item upsell para badge no carrinho
      if (discountPct) {
        const _u = _loadUpsellIds();
        _u.add(String(variantId) + ":" + discountPct);
        _saveUpsellIds(_u);
      }
      // Esconde imediatamente
      if (card) card.style.setProperty("display", "none", "important");
      // Não faz refresh próprio — o km-cart-drawer-refresh.js já escuta /cart/add
      // e fará o refresh. O evento km:cart-drawer:refreshed vai reesconder.
    } else {
      const _hr = _loadHidden(); _hr.delete(variantId); _saveHidden(_hr);
      // Remove do set de upsell ids
      const _u = _loadUpsellIds();
      for (const entry of _u) { if (entry.startsWith(String(variantId) + ":")) _u.delete(entry); }
      _saveUpsellIds(_u);
      await removeVariantFromCart(variantId);
      // Aqui sim precisa de refresh para mostrar o item de volta
      document.dispatchEvent(new CustomEvent("cart:updated"));
    }
  } catch (err) {
    console.error("[km-upsell] toggle error:", err);
    const _he = _loadHidden(); _he.delete(variantId); _saveHidden(_he);
  } finally {
    btn.classList.remove("is-loading");
  }
});

// Variant select change
document.addEventListener("change", (e) => {
  const sel = e.target.closest("[data-km-upsell-variant]");
  if (!sel) return;
  const card = sel.closest("[data-km-upsell-card]");
  const btn = card?.querySelector("[data-km-upsell-toggle]");
  if (!btn) return;
  btn.setAttribute("data-km-variant-id", sel.value);
  btn.setAttribute("aria-pressed", "false");
});

// Limpa upsell IDs cujos variants nao estao mais no carrinho real (via /cart.js)
async function _syncUpsellIdsWithDOM() {
  const upsellIds = _loadUpsellIds();
  if (!upsellIds.size) return;

  try {
    const res = await fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
    const cart = await res.json();
    const cartVids = new Set(cart.items.map((i) => String(i.variant_id)));

    let changed = false;
    for (const entry of upsellIds) {
      const vid = entry.split(":")[0];
      if (!cartVids.has(vid)) {
        upsellIds.delete(entry);
        changed = true;
      }
    }
    if (changed) _saveUpsellIds(upsellIds);

    // Se carrinho nao tem nenhum dos itens upsell, remove badges do DOM
    if (!upsellIds.size) {
      document.querySelectorAll(".km-upsell-item-badge, .km-upsell-compare").forEach((el) => el.remove());
    }
  } catch {}
}

// ── Badge nos itens do carrinho que vieram do upsell ───────────────────────
function injectUpsellBadges() {
  const upsellIds = _loadUpsellIds();
  if (!upsellIds.size) return;

  // Monta mapa variantId → pct
  const idToPct = new Map();
  for (const entry of upsellIds) {
    const colonIdx = entry.lastIndexOf(":");
    if (colonIdx > 0) {
      const vid = entry.substring(0, colonIdx);
      const pct = entry.substring(colonIdx + 1);
      idToPct.set(vid, pct);
    }
  }
  if (!idToPct.size) return;

  const drawer = document.querySelector("#km-cart-drawer") || document;
  const processedRows = new WeakSet();

  // O tema usa links com ?variant=ID — unica forma confiavel de achar o variantId
  drawer.querySelectorAll("a[href*='variant=']").forEach((link) => {
    const m = (link.href || "").match(/[?&]variant=(\d+)/);
    if (!m) return;
    const vid = m[1];
    if (!idToPct.has(vid)) return;

    // Sobe ate a linha do item
    const row = link.closest("tr, [class*='table-row'], [class*='cart-item'], [data-cart-item], li");
    if (!row || processedRows.has(row)) return;
    processedRows.add(row);

    const pct = idToPct.get(vid);

    // Esconde tags de desconto server-rendered neste item upsell especifico
    row.querySelectorAll([
      ".cart-items__discount",
      ".discount-badge",
      ".discounts",
      ".discount",
      "[class*='discount-item']",
      "[class*='cart-discount']",
      ".cart-items__discounts",
      ".km-cart-badge",           // badge -X% da thumbnail
      "[class*='cart-item-discount']",
      ".cart-item__discounts",
    ].join(",")).forEach((el) => {
      el.style.setProperty("display", "none", "important");
    });

    // Injeta nosso badge se ainda nao existir
    if (!row.querySelector(".km-upsell-item-badge")) {
      const badge = document.createElement("span");
      badge.className = "km-upsell-item-badge";
      badge.textContent = "+" + pct + "% OFF";
      const details = row.querySelector(".cart-items__details");
      if (details) details.appendChild(badge);
    }

    // Injeta compare price e atualiza preco atual com desconto
    const priceCell = row.querySelector(".cart-items__price");
    if (priceCell && !priceCell.querySelector(".km-upsell-compare")) {
      // Pega o elemento de preco principal
      const currentPriceEl = priceCell.querySelector("[class*='price'], strong, span");
      if (currentPriceEl) {
        const originalText = currentPriceEl.textContent.trim();
        // Parse do valor original
        const numericStr = originalText.replace(/[^\d,\.]/g, "").replace(",", ".");
        const originalVal = parseFloat(numericStr);
        if (!isNaN(originalVal)) {
          const discountedVal = originalVal * (1 - parseFloat(pct) / 100);
          // Extrai simbolo da moeda
          const currencySymbol = originalText.match(/^[^\d]+/)?.[0] || "R$ ";
          const discountedFormatted = currencySymbol + discountedVal.toFixed(2).replace(".", ",");
          // Injeta o preco original como riscado
          const compareEl = document.createElement("s");
          compareEl.className = "km-upsell-compare";
          compareEl.textContent = originalText;
          priceCell.insertBefore(compareEl, priceCell.firstChild);
          // Atualiza o preco atual para o valor com desconto
          currentPriceEl.textContent = discountedFormatted;
        }
      }
    }
  });
}

// ── Init ───────────────────────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", async () => {
  await _syncHiddenWithCart();
  initCarousels();
  injectUpsellBadges();
});
document.addEventListener("shopify:section:load", initCarousels);
document.addEventListener("km:cart-drawer:refreshed", () => {
  setTimeout(initCarousels, 50);
  setTimeout(injectUpsellBadges, 100);
});